import { DUPE_API_URL } from '@dupe/common/constants'
import fetcher from '~/src/lib/fetcher'
import logger from '~/src/lib/logger'

export interface UploadImageResponse {
  imageUrl: string
  hash: string
  fileId: string
  parsedData?: {
    category: string
    description: string
    mainObject: string
  }
}

export interface UploadImageOptions {
  enableVisionProcessing?: boolean
  authUser?: any
  /** Abort signal so the caller can cancel a slow upload. */
  signal?: AbortSignal
  /**
   * Fires as bytes flow out. `loaded` / `total` are the axios shape. `total`
   * is undefined for chunked requests or when the browser doesn't know the
   * body size ahead of time — callers should guard accordingly.
   */
  onProgress?: (progress: { loaded: number; total?: number }) => void
}

/**
 * Upload image via `/api/upload-image`.
 *
 * Consolidates ImageKit upload + hash generation server-side. Can optionally
 * run AI vision processing inline when `enableVisionProcessing: true`.
 *
 * Timeout is set to 90s (vs the 30s default for regular API calls). Large
 * camera-roll uploads — even post-client-side-resize — can occasionally
 * exceed the shorter cap on slow upstream links, and the server has no
 * equivalent timeout on its ImageKit call.
 */
export const uploadImageViaAPI = async (
  fileData: string,
  options: UploadImageOptions = {}
): Promise<UploadImageResponse> => {
  const {
    enableVisionProcessing = false,
    authUser,
    signal,
    onProgress,
  } = options

  try {
    logger.debug('🖼️ Upload Image API - Starting upload', {
      fileDataLength: fileData.length,
      enableVisionProcessing,
    })

    const response = await fetcher<UploadImageResponse>(
      `${DUPE_API_URL}/api/upload-image`,
      {
        method: 'POST',
        authUser,
        dataOrParams: {
          fileData,
          enableVisionProcessing,
        },
        axiosConfig: {
          // 90s — see file-level comment on uploadImageViaAPI.
          timeout: 90_000,
          ...(signal ? { signal } : {}),
          ...(onProgress
            ? {
                onUploadProgress: (evt) =>
                  onProgress({ loaded: evt.loaded, total: evt.total }),
              }
            : {}),
        },
      }
    )

    if (!response.imageUrl || !response.hash) {
      throw new Error('Invalid response from upload API')
    }

    logger.debug('✅ Upload Image API - Success', {
      imageUrl: response.imageUrl,
      hash: response.hash,
    })

    return response
  } catch (error: any) {
    logger.error('🚨 Upload Image API - Error', {
      error: error.message,
      fileDataLength: fileData.length,
      enableVisionProcessing,
    })
    throw error
  }
}

/**
 * Sibling of `uploadImageViaAPI` that re-hosts a remote image URL to
 * ImageKit by passing the URL to `/api/upload-image` (server-side
 * fetch + ImageKit upload). Used by v2 chat's "more like this" click
 * to convert Bing / gstatic / wfcdn product thumbnails into stable
 * `ik.imagekit.io` URLs so the regular image-upload flow renders
 * thumbs reliably and `/api/dupes` runs visual similarity against a
 * canonical asset. Skips client-side fetch (most product CDNs block
 * cross-origin reads) so the round-trip is single-hop.
 */
export const uploadImageFromUrlViaAPI = async (
  imageUrl: string,
  options: Omit<UploadImageOptions, 'onProgress'> = {}
): Promise<UploadImageResponse> => {
  const { enableVisionProcessing = false, authUser, signal } = options

  const response = await fetcher<UploadImageResponse>(
    `${DUPE_API_URL}/api/upload-image`,
    {
      method: 'POST',
      authUser,
      dataOrParams: {
        url: imageUrl,
        enableVisionProcessing,
      },
      axiosConfig: {
        timeout: 30_000,
        ...(signal ? { signal } : {}),
      },
    }
  )

  if (!response.imageUrl || !response.hash) {
    throw new Error('Invalid response from upload-from-url API')
  }
  return response
}
